import React, { useState } from 'react';
import Button from '../components/Button';
import Caret from '../components/Caret';
import { MenuItemRadio } from '../components/Menu';
import OverflowMenu from '../components/OverflowMenu';
import formatBoundedCount from '../utils/formatBoundedCount';
import * as styles from './PostSectionTitleFilterMenu.module.scss';

interface PostSectionTitleFilterMenuProps<
  T extends string,
  Option extends { label: T; icon: string },
> {
  options: readonly Option[];
  getCount: (option: Option) => number;
  selectedOption: Option;
  onSelectedOptionChange: (option: Option) => void;
  /**
   * Callback to trigger fetching post counts for all filter options.
   * Called on pointer down (for prefetching) or when the menu opens (as fallback).
   */
  onShouldPrefetch: () => void;
}

/**
 * A menu button component for filtering posts in a section title.
 *
 * Displays the currently selected filter option with a dropdown caret.
 * When opened, shows all available filter options with their respective post counts.
 * Each option is displayed as a radio menu item with an icon and count badge.
 */
const PostSectionTitleFilterMenu = <
  T extends string,
  Option extends { label: T; icon: string },
>({
  options,
  getCount,
  selectedOption,
  onSelectedOptionChange,
  onShouldPrefetch,
}: PostSectionTitleFilterMenuProps<T, Option>) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={styles.container}>
      <OverflowMenu
        placement="bottom-start"
        offset={8}
        open={isOpen}
        onOpenChange={(newValue) => {
          setIsOpen(newValue);

          if (newValue) {
            onShouldPrefetch();
          }
        }}
        button={
          <Button
            className={styles.roleButton}
            onPointerDown={(e) => {
              if (e.button === 0) {
                onShouldPrefetch();
              }
            }}
          >
            {selectedOption.label}
            <Caret direction={isOpen ? 'up' : 'down'} size={10} />
          </Button>
        }
      >
        {options.map((option) => {
          const count = getCount(option);

          return (
            <MenuItemRadio
              key={option.label}
              icon={option.icon}
              checked={selectedOption.label === option.label}
              onClick={() => onSelectedOptionChange(option)}
              variant="highlight"
              right={
                <div className={styles.filterCount}>
                  {formatBoundedCount(count, 99)}
                </div>
              }
            >
              {option.label}
            </MenuItemRadio>
          );
        })}
      </OverflowMenu>
    </div>
  );
};

export default PostSectionTitleFilterMenu;
